import { useCallback } from 'react';

import type { ChangedBy } from '@frontend/services';

import { FieldSuggestionOption } from '../InputGroupWithSuggestions';

type UseOnKeyDownProps = {
  isOpen: boolean;
  menuFocus: number;
  suggestions: FieldSuggestionOption[];
  defaultOptionContentValue: string;
  handleClose: (submitValue?: string | null, changedBy?: ChangedBy) => void;
  handleOpen: () => void;
  setMenuFocus: React.Dispatch<React.SetStateAction<number>>;
  setLocalValue: React.Dispatch<React.SetStateAction<string | null>>;
};

export const useOnKeyDown = ({
  isOpen,
  menuFocus,
  suggestions,
  defaultOptionContentValue,
  handleClose,
  handleOpen,
  setMenuFocus,
  setLocalValue,
}: UseOnKeyDownProps) =>
  useCallback(
    (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const keyMap: Record<
        string,
        (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void
      > = {
        Tab: ({ key, shiftKey }) => {
          isOpen && handleClose(undefined, { key, shiftKey });
        },
        Escape: ({ key, shiftKey }) => {
          isOpen && handleClose(undefined, { key, shiftKey });
        },
        ArrowDown: (
          e: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>,
        ) => {
          // If Menu is open, focus next MenuItem or loop to beginning (uses <= suggestions.length because of default value)
          if (isOpen) {
            // prevent default to remove effect on input/textarea
            e.preventDefault();
            setMenuFocus((ind) =>
              ind + 1 <= suggestions.length ? ind + 1 : 0,
            );
          } else {
            setMenuFocus(0);
            handleOpen();
          }
        },
        ArrowUp: (
          e: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>,
        ) => {
          // If Menu is open, focus prev MenuItem or loop to end (suggestions.length because of default value)
          if (isOpen) {
            // prevent default to remove effect on input/textarea
            e.preventDefault();
            setMenuFocus((ind) =>
              ind - 1 >= 0 ? ind - 1 : suggestions.length,
            );
          } else {
            setMenuFocus(suggestions.length);
            handleOpen();
          }
        },
        Enter: (
          e: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>,
        ) => {
          const { shiftKey, key } = e;
          // If shift+enter, allow normal textarea behavior (new line)
          if (shiftKey) {
            return;
          }
          // prevent default to remove effect on input/textarea
          e.preventDefault();
          // If valid MenuItem is selected, set the local value, conditionally set the accepted suggestion, and close Menu
          if (isOpen) {
            let submitValue = null;
            if (menuFocus === suggestions.length) {
              submitValue = defaultOptionContentValue;
            } else if (menuFocus >= 0 && menuFocus < suggestions.length) {
              submitValue = suggestions[menuFocus].value;
            }
            setLocalValue(submitValue);
            handleClose(submitValue, { key, shiftKey });
          } else {
            setMenuFocus(0);
            handleOpen();
          }
        },
      };

      keyMap[e.key]?.(e);
    },
    [
      isOpen,
      menuFocus,
      suggestions,
      defaultOptionContentValue,
      handleClose,
      handleOpen,
      setLocalValue,
      setMenuFocus,
    ],
  );
